import { useEffect, useMemo, useRef, useState } from "react";
import { useSearchParams } from "react-router-dom";
import dayjs from "dayjs";
import { api } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { ShiftDrawer } from "@/components/ShiftDrawer";
import { Combobox } from "@/components/Combobox";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Popover, PopoverTrigger, PopoverContent } from "@/components/ui/popover";
import { HoverCard, HoverCardTrigger, HoverCardContent } from "@/components/ui/hover-card";
import { Calendar } from "@/components/ui/calendar";
import { periodStart, periodDays, fmtTime } from "@/lib/format";
import { boardStyle, BOARD_COLOURS, BOARD_LEGEND, RULE_LABELS } from "@/lib/status";
import { ChevronLeft, ChevronRight, ChevronDown, Plus, Loader2, ShieldAlert, TriangleAlert, CheckSquare, UserX, Users, User, LayoutTemplate, CalendarOff, CalendarX2, CalendarHeart, MapPinOff, Briefcase, Home, Moon, Link2, Car, RefreshCw } from "lucide-react";
import { toast } from "sonner";
import { NotifyCheckboxes, toastNotifications } from "@/components/shift/NotifyCheckboxes";
import { orgNowLocal, orgToday } from "@/lib/tz";
import { ContactCardTrigger } from "@/components/ContactCard";

const STATUSES = ["Draft", "Published", "In Progress", "Completed", "Cancelled", "Archived"];

// Row key for the "Problem unapproved" bucket (a real worker id can never be this).
const PROBLEM_ROW = "__problem__";
const INTERNAL_ROW = "__internal__";
// Every worker on a shift (primary first). A multi-worker shift belongs to each.
const workerIds = (s) => (s.worker_ids?.length ? s.worker_ids : (s.support_worker ? [s.support_worker] : []));

// A manager has taken responsibility for the problem: they either overrode the
// block with a written reason, or the timesheet has been approved anyway.
const signedOff = (s) => !!s.override_active || s.timesheet_status === "Approved";

export default function RosterBoard() {
  const { can } = useAuth();
  const [searchParams, setSearchParams] = useSearchParams();
  const initShiftRef = useRef(searchParams.get("shift") || null);
  const [fortnight, setFortnight] = useState(true);
  const [viewBy, setViewBy] = useState("staff");
  const [anchor, setAnchor] = useState(orgNowLocal());
  const [shifts, setShifts] = useState([]);
  const [leaves, setLeaves] = useState([]);
  const [unavail, setUnavail] = useState([]);
  const [ruleIssues, setRuleIssues] = useState({});
  const [travel, setTravel] = useState({});   // shift -> drive to that worker's next shift
  const [issuesLoading, setIssuesLoading] = useState(false);
  const [participants, setParticipants] = useState([]);
  const [staff, setStaff] = useState([]);
  const [properties, setProperties] = useState([]);
  const [services, setServices] = useState([]);
  const [itemGroups, setItemGroups] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filters, setFilters] = useState({ participant: null, worker: null, status: "" });
  // Client-side filter: only shifts whose location was typed in rather than picked from
  // the participant's saved addresses. Saved lists are fetched lazily and cached by participant.
  const [manualAddrOnly, setManualAddrOnly] = useState(false);
  const [shiftIdQuery, setShiftIdQuery] = useState("");
  const [shiftHits, setShiftHits] = useState(null);   // null = closed
  useEffect(() => {
    const q = shiftIdQuery.trim();
    if (q.length < 2) { setShiftHits(null); return undefined; }
    let live = true;
    const t = setTimeout(() => {
      api.searchShifts(q).then((r) => { if (live) setShiftHits(r); }).catch(() => { if (live) setShiftHits([]); });
    }, 250);
    return () => { live = false; clearTimeout(t); };
  }, [shiftIdQuery]);
  const jumpToShift = (h) => {
    // Clear filters that could hide the shift, move to its period, then open it.
    setFilters({ participant: null, worker: null, status: "" });
    setManualAddrOnly(false);
    setAnchor(dayjs(String(h.shift_date).slice(0, 10)));
    setDrawer({ open: true, name: h.name, defaults: null });
    setShiftIdQuery(""); setShiftHits(null);
  };
  const [savedAddrs, setSavedAddrs] = useState({});
  // Staff-view-only row filters (narrow WHICH workers appear): by a client's / SIL
  // house's preferred-staff list, and by gender. Kept separate from `filters` so
  // they don't retrigger the shift query (they only affect row rendering).
  const [staffFilter, setStaffFilter] = useState({ preferredFor: null, gender: "" });
  // Public holidays per holiday list (region) for the period shown, and the
  // holiday-list filter (ISSUE-00317).
  const [holidayLists, setHolidayLists] = useState([]);
  const [holidayFilter, setHolidayFilter] = useState("");
  const [preferredIds, setPreferredIds] = useState(null); // null = off, Set = allowed staff ids
  const [drawer, setDrawer] = useState({ open: false, name: null, defaults: null });
  const [bulkMode, setBulkMode] = useState(false);
  const [selected, setSelected] = useState(new Set());
  const [confirmPublish, setConfirmPublish] = useState(false);
  const [notifyStaff, setNotifyStaff] = useState(false);
  const [notifyParticipants, setNotifyParticipants] = useState(false);
  const [saveTmpl, setSaveTmpl] = useState(null);
  const [tmplList, setTmplList] = useState([]);
  const [savingTmpl, setSavingTmpl] = useState(false);
  const [tmplConflicts, setTmplConflicts] = useState(null);
  const [weekAnchor, setWeekAnchor] = useState("2026-08-21");
  const [calOpen, setCalOpen] = useState(false);

  useEffect(() => {
    api.ruleConfig().then((c) => c?.roster_week_anchor && setWeekAnchor(c.roster_week_anchor)).catch(() => {});
  }, []);

  // Rotating 4-week cycle label for a given Friday, anchored to the configured
  // Week-1 Friday (Week 1→2→3→4→1…).
  const cycleWeek = (friday) => {
    const base = periodStart(dayjs(weekAnchor), false); // Friday of the anchor week
    const weeks = friday.diff(base, "week");
    return ((weeks % 4) + 4) % 4 + 1;
  };

  const openSaveTemplate = () => {
    const isSil = viewBy === "sil";
    setSaveTmpl({ kind: isSil ? "sil" : "participant", participant: isSil ? null : (filters.participant || null),
      property: null, mode: "new", template_name: "", template: null, week: 1, week_mode: "overwrite" });
    setTmplConflicts(null);
    api.templates().then(setTmplList).catch(() => setTmplList([]));
  };
  const runSaveTemplate = async (conflict_action) => {
    const f = saveTmpl;
    setSavingTmpl(true);
    try {
      const res = await api.templateFromRoster({
        participant: f.kind === "participant" ? f.participant : undefined,
        property: f.kind === "sil" ? f.property : undefined,
        friday: start.format("YYYY-MM-DD"), week: Number(f.week),
        template: f.mode === "existing" ? f.template : undefined,
        template_name: f.mode === "new" ? f.template_name.trim() : undefined,
        week_mode: f.mode === "existing" ? f.week_mode : undefined,
        conflict_action,
      });
      if (res?.status === "conflicts") { setSavingTmpl(false); setTmplConflicts(res.conflicts); return; }
      toast.success(`Week saved to template (Week ${f.week})`);
      setTmplConflicts(null); setSaveTmpl(null);
    } catch (e) { toast.error(e.response?.data?.detail || "Could not save template"); }
    finally { setSavingTmpl(false); }
  };
  const doSaveTemplate = () => {
    const f = saveTmpl;
    if (f.kind === "sil" ? !f.property : !f.participant) return toast.error(f.kind === "sil" ? "Choose a SIL house" : "Choose a participant");
    if (f.mode === "new" && !f.template_name.trim()) return toast.error("Enter a template name");
    if (f.mode === "existing" && !f.template) return toast.error("Choose a template");
    return runSaveTemplate(undefined);
  };

  const start = useMemo(() => periodStart(anchor, fortnight), [anchor, fortnight]);
  const days = useMemo(() => periodDays(start, fortnight), [start, fortnight]);
  const step = fortnight ? 14 : 7;

  useEffect(() => {
    api.holidaysInRange(days[0].format("YYYY-MM-DD"), days[days.length - 1].format("YYYY-MM-DD"))
      .then(setHolidayLists).catch(() => setHolidayLists([]));
  }, [days]);
  const holidayDays = useMemo(() => Object.fromEntries(holidayLists.map((l) => [l.name, l.days])), [holidayLists]);
  const partHoliday = useMemo(() => Object.fromEntries(participants.map((p) => [p.name, p.holiday_list])), [participants]);
  const holidayText = (h) => `${h.description}${h.start_time ? ` (${String(h.start_time).slice(0, 5)}–${String(h.end_time).slice(0, 5)})` : ""}`;
  // A date's holidays across lists (only the filtered list when one is chosen), for the staff view header.
  const headerHolidays = (ds) => holidayLists
    .filter((l) => l.days[ds] && (!holidayFilter || l.name === holidayFilter))
    .map((l) => `${holidayText(l.days[ds])}: ${l.title}`);

  useEffect(() => {
    Promise.all([api.participants(), api.staff(), api.serviceItems(), api.itemGroups()]).then(([p, s, sv, ig]) => {
      setParticipants(p); setStaff(s); setServices(sv); setItemGroups(ig);
    });
    api.properties({ status: "Active" }).then(setProperties).catch(() => {});
  }, []);

  // If the page was opened with ?shift=<name> (e.g. from a Dashboard "active now"
  // row click), open the ShiftDrawer for that shift as soon as the support data
  // (participants + staff) needed by the drawer is available.  The ref means we
  // only act once; clearing the URL param prevents the drawer re-opening on a
  // subsequent refresh or back-navigation.
  useEffect(() => {
    const name = initShiftRef.current;
    if (!name || !participants.length || !staff.length) return;
    initShiftRef.current = null; // consume — only open once
    setDrawer({ open: true, name, defaults: null });
    setSearchParams((prev) => { prev.delete("shift"); return prev; }, { replace: true });
  }, [participants.length, staff.length]); // eslint-disable-line

  // Resolve the selected "preferred staff for…" entity into a set of allowed
  // staff ids (participant or SIL house preferred_staff list).
  useEffect(() => {
    const pf = staffFilter.preferredFor;
    if (!pf) { setPreferredIds(null); return; }
    const kind = pf.slice(0, 1), id = pf.slice(2);
    const req = kind === "h" ? api.property(id) : api.participant(id);
    req.then((doc) => {
      const ids = (doc?.preferred_staff || []).map((r) => r.staff).filter(Boolean);
      setPreferredIds(new Set(ids));
    }).catch(() => setPreferredIds(new Set()));
  }, [staffFilter.preferredFor]);

  // quiet: refresh after a save without the spinner, so the board keeps its scroll position.
  const load = (quiet) => {
    const ctrl = new AbortController();
    if (quiet !== true) setLoading(true);
    api.shifts({
      // Fetch one day earlier so an overnight shift starting on the last day of
      // the PREVIOUS period (ending after midnight on day 1 here) still renders
      // its "end" half on this period's first day. The visible `days` array
      // starts at `start`, so the spill-in shift only shows its end part.
      date_from: start.subtract(1, "day").format("YYYY-MM-DD"),
      date_to: start.add(step - 1, "day").format("YYYY-MM-DD"),
      participant: filters.participant || undefined,
      support_worker: filters.worker || undefined,
      status: filters.status && filters.status !== "__job_board__" ? filters.status : undefined,
    }, ctrl.signal)
      .then(setShifts)
      .catch((e) => { if (e?.code !== "ERR_CANCELED" && e?.name !== "CanceledError") toast.error("Failed to load roster"); })
      .finally(() => { if (!ctrl.signal.aborted) setLoading(false); });
    return () => ctrl.abort();
  };
  // Leave / unavailability / rule issues drive the card colours, so they must be
  // re-read whenever shifts are reloaded — otherwise a stale issue (e.g. the leave
  // clash of a worker who has just been removed) keeps the card red.
  const loadIssues = () => {
    const ctrl = new AbortController();
    const df = start.format("YYYY-MM-DD"), dt = start.add(step - 1, "day").format("YYYY-MM-DD");
    api.leave({ date_from: df, date_to: dt, include_expired: true }).then(setLeaves).catch(() => setLeaves([]));
    api.unavailabilityCalendar(df, dt).then(setUnavail).catch(() => setUnavail([]));
    api.travelLegs({ date_from: df, date_to: dt }).then(setTravel).catch(() => setTravel({}));
    setIssuesLoading(true);
    api.boardIssues({
      date_from: start.subtract(1, "day").format("YYYY-MM-DD"), date_to: dt,
      participant: filters.participant || undefined,
      support_worker: filters.worker || undefined,
      status: filters.status && filters.status !== "__job_board__" ? filters.status : undefined,
    }, ctrl.signal)
      .then((r) => { setRuleIssues(r); setIssuesLoading(false); })
      .catch((e) => { if (e?.code !== "ERR_CANCELED" && e?.name !== "CanceledError") setIssuesLoading(false); });
    return () => ctrl.abort();
  };

  const refresh = () => { load(true); loadIssues(); };

  useEffect(load, [start, step, filters, viewBy]); // eslint-disable-line
  useEffect(() => {
    if (!manualAddrOnly) return;
    const missing = [...new Set(shifts.flatMap((s) => s.participant_ids || []))].filter((p) => !(p in savedAddrs));
    if (!missing.length) return;
    Promise.all(missing.map((p) => api.participantAddresses(p)
      .then((a) => [p, a.map((x) => normAddr(x.address))]).catch(() => [p, []])))
      .then((pairs) => setSavedAddrs((m) => ({ ...m, ...Object.fromEntries(pairs) })));
  }, [manualAddrOnly, shifts]); // eslint-disable-line
  useEffect(() => { setRuleIssues({}); return loadIssues(); }, [start, step, filters, viewBy]); // eslint-disable-line

  const leaveCell = (workerId, day) => {
    if (viewBy !== "staff" || !workerId) return [];
    const ds = day.format("YYYY-MM-DD");
    return leaves.filter((l) => l.support_worker === workerId &&
      (l.status === "Approved" || l.status === "Pending" || l.status === "Expired") &&
      ds >= l.from_date && ds <= l.to_date);
  };

  const unavailCell = (workerId, day) => {
    if (viewBy !== "staff" || !workerId) return [];
    const ds = day.format("YYYY-MM-DD");
    return unavail.filter((u) => u.support_worker === workerId && u.date === ds);
  };

  // Rows depend on view mode; always show ALL active + an Unassigned bucket (staff view).
  const rows = useMemo(() => {
    if (viewBy === "sil") {
      return properties.map((p) => ({ id: p.name, name: p.property_name, sub: p.suburb }));
    }
    if (viewBy === "participant") {
      let list = participants.filter((p) => p.status === "Active");
      if (filters.participant) list = list.filter((p) => p.name === filters.participant);
      if (holidayFilter) list = list.filter((p) => p.holiday_list === holidayFilter);
      const arr = list.map((p) => ({ id: p.name, name: p.display_name || p.name, photo: p.photo, holiday: p.holiday_list }));
      if (!filters.participant) arr.push({ id: INTERNAL_ROW, name: "Internal (no participant)", sub: "Staff Training / Office", internal: true });
      return arr;
    }
    let list = staff.filter((s) => s.status === "Active");
    if (filters.worker) list = list.filter((s) => s.name === filters.worker);
    if (staffFilter.gender) list = list.filter((s) => (s.gender || "") === staffFilter.gender);
    if (preferredIds) list = list.filter((s) => preferredIds.has(s.name));
    const arr = list.map((s) => ({ id: s.name, name: s.display_name || s.name, role: s.primary_role, photo: s.photo }));
    if (!filters.worker && !staffFilter.gender && !preferredIds) {
      arr.unshift({ id: null, name: "Unassigned / Open", unassigned: true });
      arr.unshift({ id: PROBLEM_ROW, name: "Problem unapproved", problem: true });
    }
    return arr;
  }, [viewBy, staff, participants, properties, filters, staffFilter, preferredIds, holidayFilter]);

  const keyField = viewBy === "participant" ? "participant" : viewBy === "sil" ? "sil_property" : "support_worker";

  // Per-worker ACTIVE hours for the shown period (staff view). Excludes inactive
  // sleepover time (the recorded sleep window); overnights counted separately.
  // Mirrors the backend overtime rule so the board shows what it's flagging.
  const workerHours = useMemo(() => {
    const m = {};
    if (viewBy !== "staff") return m;
    const dset = new Set(days.map((d) => d.format("YYYY-MM-DD")));
    const sleepH = (s) => {
      if (!s.is_sleepover) return 0;
      const a = s.sleep_start_time ? dayjs(s.sleep_start_time) : null;
      let b = s.sleep_end_time ? dayjs(s.sleep_end_time) : null;
      if (a && b && a.isValid() && b.isValid()) {
        if (!b.isAfter(a)) b = b.add(1, "day");
        return Math.max(0, b.diff(a, "minute") / 60);
      }
      return 8;
    };
    shifts.forEach((s) => {
      if (["Cancelled", "Archived"].includes(s.status)) return;
      if (!dset.has(String(s.shift_date))) return;
      const active = Math.max(0, (Number(s.rostered_hours) || 0) - sleepH(s));
      workerIds(s).forEach((w) => {
        const e = m[w] || { hours: 0, on: 0 };
        e.hours += active;
        if (s.is_sleepover) e.on += 1;
        m[w] = e;
      });
    });
    return m;
  }, [shifts, viewBy, days]);

  // Legs of each linked package, grouped from the already-loaded board feed
  // (no extra fetch) so a card can preview its siblings on hover.
  const linkGroups = useMemo(() => {
    const m = {};
    for (const s of shifts) {
      if (!s.link_group_id) continue;
      (m[s.link_group_id] ||= []).push(s);
    }
    for (const g of Object.values(m)) g.sort((a, b) => String(a.start_time).localeCompare(String(b.start_time)));
    return m;
  }, [shifts]);

  // A shift is overnight when its end falls on a later calendar day than its start.
  const isOvernight = (s) =>
    !!(s.start_time && s.end_time &&
       dayjs(s.end_time).format("YYYY-MM-DD") > dayjs(s.start_time).format("YYYY-MM-DD"));

  const normAddr = (a) => String(a || "").trim().replace(/\s+/g, " ").toLowerCase();
  const hasManualAddress = (s) => {
    const loc = normAddr(s.location);
    if (!loc) return false;
    return !(s.participant_ids || []).some((p) => (savedAddrs[p] || []).includes(loc));
  };

  const ownsShift = (s, rowId) => {
    if (filters.status === "__job_board__" && !s.job_board_open) return false;
    if (manualAddrOnly && !hasManualAddress(s)) return false;
    if (holidayFilter && !(s.participant_ids || []).some((p) => partHoliday[p] === holidayFilter)) return false;
    const owner = s[keyField] || null;
    const published = !!s.published || ["Published", "In Progress", "Completed"].includes(s.status);
    // "Problem unapproved": staffed, published shifts carrying a problem that no
    // manager has signed off yet. They sit here instead of the worker's column
    // so nobody mistakes them for settled work.
    if (rowId === PROBLEM_ROW) {
      // "Problem unapproved" bucket: staffed, published shifts with an active
      // problem that no manager has signed off yet.  The shift ALSO remains
      // visible in the worker's own row (additive, not exclusive) — once a
      // manager approves/overrides the problem (signedOff), it drops out of
      // this bucket only; the worker's row always keeps it.
      return viewBy === "staff" && !!owner
        && issueSet.has(s.name) && !signedOff(s);
    }
    if (owner === rowId) {
      // Always render in the worker's own row regardless of problem / approval
      // state.  Unapproved-problem shifts appear here AND in PROBLEM_ROW
      // simultaneously so the worker's schedule is always complete.
      return true;
    }
    if (viewBy === "staff" && rowId && workerIds(s).includes(rowId)) return true;
    if (viewBy === "participant" && rowId === INTERNAL_ROW) return !!s.no_participant;
    if (viewBy === "participant" && rowId && Array.isArray(s.participant_ids) && s.participant_ids.includes(rowId)) return true;
    return false;
  };

  // Returns { s, part } entries. Overnight shifts render on BOTH the start day
  // ("start") and the following day ("end"); everything else is a "single".
  const cell = (rowId, day) => {
    const out = [];
    for (const s of shifts) {
      if (!ownsShift(s, rowId)) continue;
      const startsHere = dayjs(s.shift_date).isSame(day, "day");
      const overnight = isOvernight(s);
      if (startsHere) out.push({ s, part: overnight ? "start" : "single" });
      else if (overnight && dayjs(s.end_time).isSame(day, "day")) out.push({ s, part: "end" });
    }
    return out;
  };

  // Client-side issue detection for colour coding — reuses leave/unavailability
  // already loaded for the board plus in-memory double-booking detection.
  const issueSet = useMemo(() => {
    const mins = (t) => { const [h, m] = String(t || "").padStart(8, "0").split(":"); return (+h) * 60 + (+m); };
    const win = (dt, ds) => dayjs(dt).diff(dayjs(`${ds} 00:00:00`), "minute");
    const ov = (a1, a2, b1, b2) => a1 < b2 && b1 < a2;
    const active = shifts.filter((s) => !["Cancelled", "Archived"].includes(s.status));
    const bad = new Set();
    for (const s of shifts) {
      if (["Cancelled", "Archived"].includes(s.status)) continue;
      const ds = dayjs(s.shift_date).format("YYYY-MM-DD");
      const ws = workerIds(s);
      const worker = ws.length > 0;
      const sS = win(s.start_time, ds), sE = win(s.end_time, ds);
      let issue = !!s.has_incident || (!!s.flag_issue && !!worker);
      if (worker && !issue) {
        // Approved leave
        for (const l of leaves) {
          if (!ws.includes(l.support_worker) || l.status !== "Approved") continue;
          if (!(ds >= l.from_date && ds <= l.to_date)) continue;
          if (l.full_day) { issue = true; break; }
          let le = mins(l.end_time); const lst = mins(l.start_time); if (le <= lst) le += 1440;
          if (ov(sS, sE, lst, le)) { issue = true; break; }
        }
      }
      if (worker && !issue) {
        // Ongoing unavailability (skip "Maybe Available" — that's a soft warning, not a hard board issue)
        for (const u of unavail) {
          if (!ws.includes(u.support_worker) || u.date !== ds) continue;
          if (u.availability_type === "Maybe Available") continue;
          if (u.mode === "All Day") { issue = true; break; }
          if (u.mode === "All Day Except Overnight") { if (ov(sS, sE, 360, 1320)) { issue = true; break; } continue; }
          let ue = mins(u.end_time); const ust = mins(u.start_time); if (ue <= ust) ue += 1440;
          if (ov(sS, sE, ust, ue)) { issue = true; break; }
        }
      }
      if (worker && !issue) {
        // Double booking (another active shift sharing a worker, overlapping times same day)
        for (const t of active) {
          if (t.name === s.name || !workerIds(t).some((w) => ws.includes(w))) continue;
          if (!dayjs(t.shift_date).isSame(dayjs(s.shift_date), "day")) continue;
          if (ov(sS, sE, win(t.start_time, ds), win(t.end_time, ds))) { issue = true; break; }
        }
      }
      // Server-side rule hard-blocks (expired compliance, service agreement,
      // incompatible staff, plan expiry, travel/CAS blocks, etc.).
      if (!issue && ruleIssues[s.name]?.block) issue = true;
      if (issue) bad.add(s.name);
    }
    return bad;
  }, [shifts, leaves, unavail, ruleIssues]);

  // How many staffed shifts in view are sitting in the Problem row.
  const problemCount = useMemo(() => {
    if (viewBy !== "staff") return 0;
    const dset = new Set(days.map((d) => d.format("YYYY-MM-DD")));
    return shifts.filter((s) => s.support_worker && issueSet.has(s.name) && !signedOff(s)
      && dset.has(String(s.shift_date))).length;
  }, [shifts, issueSet, viewBy, days]);

  const toggleSel = (name) => {    const n = new Set(selected);
    n.has(name) ? n.delete(name) : n.add(name);
    setSelected(n);
  };

  // A shift is a publishable draft when it has not been published yet and is not
  // Cancelled or Archived. This deliberately covers BOTH "Unpublished" (yellow)
  // AND "Unpublished — has a problem" (light_red) shifts per the board legend.
  //
  // NOTE: We intentionally do NOT gate on the current visible rows / row-filters.
  // "Select all drafts" should select every draft in the loaded period regardless
  // of which worker rows are currently visible (staff gender filter, preferred-staff
  // filter, participant/SIL view, etc.) so managers never accidentally miss drafts
  // that are scrolled off-screen or belong to a filtered-out worker.  The backend
  // bulk-publish endpoint re-validates each shift independently, so selecting an
  // "off-screen" draft is always safe.
  const isDraftShift = (s) => !s.published && !["Cancelled", "Archived"].includes(s.status);
  const allDraftNames = useMemo(() => {
    const names = new Set();
    for (const s of shifts) {
      if (isDraftShift(s)) names.add(s.name);
    }
    return names;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [shifts]);

  const doBulkPublish = () => {
    if (selected.size === 0) return toast.error("Select draft shifts first");
    setNotifyStaff(false); // always reset the notification toggles when (re-)opening the dialog
    setNotifyParticipants(false);
    setConfirmPublish(true);
  };

  const runBulkPublish = async () => {
    setConfirmPublish(false);
    const sendNotify = notifyStaff;
    const sendNotifyParticipants = notifyParticipants;
    setNotifyStaff(false); // reset so next dialog open starts unchecked
    setNotifyParticipants(false);
    const unassigned = shifts.filter((s) => selected.has(s.name) && !s.support_worker).length;
    try {
      const res = await api.bulkPublish([...selected], sendNotify, sendNotifyParticipants);
      toast.success(`Published ${res.published.length} shift(s)`);
      if (unassigned > 0) toast.warning(`${unassigned} published without an assigned worker`);
      toastNotifications(res.notifications);
      if (res.blocked.length) toast.warning(`${res.blocked.length} blocked by hard blocks — override needed`);
      setSelected(new Set()); setBulkMode(false); refresh();
    } catch { toast.error("Bulk publish failed"); }
  };

  const openCreate = (rowId, day) => {
    if (!can.has("roster.edit") || bulkMode) return;
    const defaults = { date: day.format("YYYY-MM-DD") };
    if (viewBy === "participant" && rowId === INTERNAL_ROW) defaults.no_participant = true;
    else if (viewBy === "participant" && rowId) defaults.participant = rowId;
    if (viewBy === "staff" && rowId) defaults.worker = rowId;
    if (viewBy === "sil" && rowId) defaults.sil_property = rowId;
    setDrawer({ open: true, name: null, defaults });
  };

  const pOpts = participants.map((p) => ({ value: p.name, label: p.display_name || p.name }));
  const wOpts = staff.filter((s) => s.status === "Active").map((s) => ({ value: s.name, label: s.display_name || s.name }));
  // Staff-view row-filter options
  const preferredForOpts = [
    ...participants.filter((p) => p.status === "Active").map((p) => ({ value: `p:${p.name}`, label: `Client · ${p.display_name || p.name}` })),
    ...properties.map((p) => ({ value: `h:${p.name}`, label: `SIL · ${p.property_name}` })),
  ];
  const genderOpts = [...new Set(staff.map((s) => s.gender).filter(Boolean))].sort();
  const colW = fortnight ? "minmax(78px,1fr)" : "minmax(120px,1fr)";

  return (
    <div className="p-4">
      <div className="bg-white border border-slate-200 rounded-lg mb-3">
        <div className="flex flex-wrap items-center justify-between gap-2 p-2.5 border-b border-slate-100">
          <div className="flex items-center gap-1.5">
            <Button variant="outline" size="icon" className="h-8 w-8" onClick={() => setAnchor(start.subtract(step, "day"))} data-testid="period-prev"><ChevronLeft className="h-4 w-4" /></Button>
            <Button variant="outline" size="sm" className="h-8 text-xs" onClick={() => setAnchor(orgNowLocal())} data-testid="period-today">Today</Button>
            <Button variant="outline" size="icon" className="h-8 w-8" onClick={() => setAnchor(start.add(step, "day"))} data-testid="period-next"><ChevronRight className="h-4 w-4" /></Button>
            <Popover open={calOpen} onOpenChange={setCalOpen}>
              <PopoverTrigger asChild>
                <button className="ml-2 text-left rounded-md px-2 py-1 -mx-2 -my-1 hover:bg-slate-50 transition-colors group" data-testid="period-jump-trigger" title="Jump to a specific week">
                  <div className="text-sm font-bold text-slate-900 flex items-center gap-2">
                    {fortnight ? "Fortnight" : "Week"} · {start.format("ddd D MMM")}
                    <span data-testid="roster-week-badge" className="inline-flex items-center rounded-full bg-[#bf3b4f]/10 text-[#bf3b4f] text-[10px] font-bold px-2 py-0.5 border border-[#bf3b4f]/20">
                      {fortnight
                        ? `Week ${cycleWeek(start)} & ${cycleWeek(start.add(7, "day"))}`
                        : `Week ${cycleWeek(start)}`}
                    </span>
                    <ChevronDown className="h-3.5 w-3.5 text-slate-400 group-hover:text-slate-600" />
                  </div>
                  <div className="text-[10px] text-slate-400">{start.format("D MMM")} – {start.add(step - 1, "day").format("D MMM YYYY")} (Fri–Thu)</div>
                </button>
              </PopoverTrigger>
              <PopoverContent align="start" className="w-auto p-0" data-testid="period-jump-popover">
                <div className="px-3 pt-3 pb-1 text-xs font-semibold text-slate-600">Jump to any date — the board snaps to that {fortnight ? "fortnight" : "week"} (Fri–Thu)</div>
                <Calendar
                  mode="single"
                  weekStartsOn={5}
                  selected={start.toDate()}
                  defaultMonth={start.toDate()}
                  onSelect={(d) => { if (d) { setAnchor(dayjs(d)); setCalOpen(false); } }}
                  modifiers={{ periodRange: { from: start.toDate(), to: start.add(step - 1, "day").toDate() } }}
                  modifiersClassNames={{ periodRange: "bg-[#bf3b4f]/10 text-[#bf3b4f] rounded-none" }}
                  data-testid="period-jump-calendar"
                />
                <div className="flex items-center justify-between gap-2 px-3 pb-3 pt-1 border-t border-slate-100">
                  <button className="text-xs font-medium text-slate-600 hover:text-slate-900" data-testid="period-jump-today"
                    onClick={() => { setAnchor(orgNowLocal()); setCalOpen(false); }}>Today</button>
                  <span className="text-[10px] text-slate-400">Tip: use the calendar arrows to jump months ahead</span>
                </div>
              </PopoverContent>
            </Popover>
          </div>
          <div className="flex items-center gap-2 flex-wrap">
            <div className="flex rounded-md border border-slate-200 overflow-hidden">
              <button onClick={() => setViewBy("staff")} data-testid="view-by-staff" className={`px-2.5 h-8 text-xs font-medium inline-flex items-center gap-1 ${viewBy === "staff" ? "bg-[#bf3b4f] text-white" : "bg-white text-slate-600"}`}><User className="h-3.5 w-3.5" />Staff</button>
              <button onClick={() => setViewBy("participant")} data-testid="view-by-participant" className={`px-2.5 h-8 text-xs font-medium inline-flex items-center gap-1 ${viewBy === "participant" ? "bg-[#bf3b4f] text-white" : "bg-white text-slate-600"}`}><Users className="h-3.5 w-3.5" />Participant</button>
              <button onClick={() => setViewBy("sil")} data-testid="view-by-sil" className={`px-2.5 h-8 text-xs font-medium inline-flex items-center gap-1 ${viewBy === "sil" ? "bg-[#bf3b4f] text-white" : "bg-white text-slate-600"}`}><Home className="h-3.5 w-3.5" />SIL</button>
            </div>
            <div className="flex rounded-md border border-slate-200 overflow-hidden">
              <button onClick={() => setFortnight(false)} data-testid="range-week" className={`px-2.5 h-8 text-xs font-medium ${!fortnight ? "bg-slate-800 text-white" : "bg-white text-slate-600"}`}>Week</button>
              <button onClick={() => setFortnight(true)} data-testid="range-fortnight" className={`px-2.5 h-8 text-xs font-medium ${fortnight ? "bg-slate-800 text-white" : "bg-white text-slate-600"}`}>Fortnight</button>
            </div>
            {can.has("roster.bulk_publish") && (
              <Button variant={bulkMode ? "default" : "outline"} size="sm" className="h-8 text-xs" style={bulkMode ? { backgroundColor: "#bf3b4f" } : {}}
                onClick={() => { setBulkMode(!bulkMode); setSelected(new Set()); }} data-testid="bulk-mode-toggle">
                <CheckSquare className="h-3.5 w-3.5 mr-1.5" /> {bulkMode ? `Selected ${selected.size}` : "Bulk Publish"}
              </Button>
            )}
            {bulkMode && (
              <>
                <Button variant="outline" size="sm" className="h-8 text-xs" onClick={() => setSelected(new Set(allDraftNames))} data-testid="bulk-select-all">
                  Select all drafts ({allDraftNames.size})
                </Button>
                <Button variant="outline" size="sm" className="h-8 text-xs" onClick={() => setSelected(new Set())} disabled={selected.size === 0} data-testid="bulk-clear">Clear</Button>
                <Button size="sm" className="h-8 text-xs" style={{ backgroundColor: "#0D9488" }} onClick={doBulkPublish} data-testid="bulk-publish-confirm">Publish selected</Button>
              </>
            )}
            {can.has("templates.from_roster") && !fortnight && (viewBy === "participant" || viewBy === "sil") && (
              <Button variant="outline" size="sm" className="h-8 text-xs" onClick={openSaveTemplate} data-testid="save-as-template-button">
                <LayoutTemplate className="h-3.5 w-3.5 mr-1.5" /> Save as Template
              </Button>
            )}
            {can.has("roster.edit") && (
              <Button size="sm" className="h-8 text-xs" style={{ backgroundColor: "#bf3b4f" }}
                onClick={() => setDrawer({ open: true, name: null, defaults: { date: orgToday() } })} data-testid="new-shift-button">
                <Plus className="h-3.5 w-3.5 mr-1.5" /> New Shift
              </Button>
            )}
            <Button variant="outline" size="sm" className="h-8 text-xs" onClick={() => { refresh(); toast.success("Revalidating shift issues…"); }}
              disabled={issuesLoading} data-testid="refresh-issues-button">
              <RefreshCw className={`h-3.5 w-3.5 mr-1.5 ${issuesLoading ? "animate-spin" : ""}`} /> {issuesLoading ? "Checking…" : "Refresh Issues"}
            </Button>
          </div>
        </div>
        <div className="flex flex-wrap items-center gap-2 p-2.5">
          <div className="w-52"><Combobox testid="filter-participant" options={pOpts} value={filters.participant} onChange={(v) => setFilters((f) => ({ ...f, participant: v }))} placeholder="All participants" /></div>
          <div className="w-52"><Combobox testid="filter-worker" options={wOpts} value={filters.worker} onChange={(v) => setFilters((f) => ({ ...f, worker: v }))} placeholder="All workers" /></div>
          <select data-testid="filter-status" value={filters.status} onChange={(e) => setFilters((f) => ({ ...f, status: e.target.value }))}
            className="h-9 text-xs rounded-md border border-slate-200 bg-white px-2 text-slate-700">
            <option value="">All statuses</option>
            <option value="__job_board__">On Job Board</option>
            {STATUSES.map((s) => <option key={s} value={s}>{s}</option>)}
          </select>
          <div className="relative">
            <input type="search" data-testid="filter-shift-id" value={shiftIdQuery} onChange={(e) => setShiftIdQuery(e.target.value)}
              onKeyDown={(e) => { if (e.key === "Escape") { setShiftIdQuery(""); setShiftHits(null); } }}
              placeholder="Find shift by ID…" aria-label="Find shift by ID" autoComplete="off"
              className="h-9 w-52 text-xs rounded-md border border-slate-200 bg-white px-2 text-slate-700" />
            {shiftHits && (
              <div data-testid="shift-id-results" className="absolute z-30 mt-1 w-80 max-h-72 overflow-auto rounded-md border border-slate-200 bg-white shadow-lg">
                {shiftHits.length === 0 && <div className="px-3 py-2 text-xs text-slate-500">No shifts found</div>}
                {shiftHits.map((h) => (
                  <button key={h.name} type="button" data-testid={`shift-id-result-${h.name}`} onClick={() => jumpToShift(h)}
                    className="block w-full text-left px-3 py-1.5 hover:bg-slate-50 border-b border-slate-100 last:border-0">
                    <div className="text-xs font-medium text-slate-800">{h.name} <span className="font-normal text-slate-500">· {h.status}</span></div>
                    <div className="text-[11px] text-slate-500 truncate">
                      {dayjs(String(h.shift_date).slice(0, 10)).format("ddd D MMM YYYY")} · {h.participant_name || "No participant"} · {h.support_worker_name || "Unassigned"}
                    </div>
                  </button>
                ))}
              </div>
            )}
          </div>
          {holidayLists.length > 0 && (
            <select data-testid="filter-holiday-list" value={holidayFilter} onChange={(e) => setHolidayFilter(e.target.value)}
              title="Show only participants on this holiday list (region)"
              className="h-9 text-xs rounded-md border border-slate-200 bg-white px-2 text-slate-700">
              <option value="">All holiday lists</option>
              {holidayLists.map((l) => <option key={l.name} value={l.name}>{l.title}</option>)}
            </select>
          )}
          <label className="flex items-center gap-1.5 h-9 px-2 text-xs rounded-md border border-slate-200 bg-white text-slate-700 cursor-pointer">
            <input type="checkbox" data-testid="filter-manual-address" checked={manualAddrOnly} onChange={(e) => setManualAddrOnly(e.target.checked)} />
            Manually entered address
          </label>
          {viewBy === "staff" && (
            <>
              <div className="w-56"><Combobox testid="filter-preferred-for" options={preferredForOpts} value={staffFilter.preferredFor} onChange={(v) => setStaffFilter((f) => ({ ...f, preferredFor: v }))} placeholder="Preferred staff for…" /></div>
              <select data-testid="filter-gender" value={staffFilter.gender} onChange={(e) => setStaffFilter((f) => ({ ...f, gender: e.target.value }))}
                className="h-9 text-xs rounded-md border border-slate-200 bg-white px-2 text-slate-700">
                <option value="">All genders</option>
                {genderOpts.map((g) => <option key={g} value={g}>{g} only</option>)}
              </select>
              {(staffFilter.preferredFor || staffFilter.gender) && (
                <button type="button" data-testid="filter-staff-clear" onClick={() => setStaffFilter({ preferredFor: null, gender: "" })}
                  className="h-9 px-2 text-xs text-slate-500 hover:text-slate-800 underline">Clear</button>
              )}
            </>
          )}
        </div>
      </div>

      <div className="flex flex-wrap gap-x-3 gap-y-1 mb-2 px-1" data-testid="board-legend">
        {BOARD_LEGEND.map((k) => {
          const c = BOARD_COLOURS[k];
          return <span key={k} className="inline-flex items-center gap-1 text-[10px] text-slate-500"><span className="h-2 w-2 rounded-full" style={{ backgroundColor: c.dot }} />{c.label}</span>;
        })}
        {issuesLoading && (
          <span className="inline-flex items-center gap-1 text-[10px] font-medium text-slate-500" data-testid="board-issues-loading">
            <Loader2 className="h-3 w-3 animate-spin" /> Checking for issues…
          </span>
        )}
      </div>

      <div className="bg-white border border-slate-200 rounded-lg overflow-hidden">
        {/* Header and rows share one scroller so its scrollbar narrows both grids equally and the day columns line up. */}
        <div className="overflow-auto max-h-[calc(100vh-220px)]">
          <div style={{ minWidth: fortnight ? 1300 : 1000 }}>
            <div className="grid sticky top-0 z-10 bg-slate-50 border-b border-slate-200" style={{ gridTemplateColumns: `190px repeat(${days.length}, ${colW})` }}>
              <div className="px-3 py-2 text-[10px] font-bold uppercase tracking-wider text-slate-400">{viewBy === "participant" ? "Participant" : viewBy === "sil" ? "SIL House" : "Support Worker"}</div>
              {days.map((d) => {
                const hols = viewBy === "participant" ? [] : headerHolidays(d.format("YYYY-MM-DD"));
                return (
                  <div key={d.format()} title={hols.length ? `Public holiday\n${hols.join("\n")}` : undefined}
                    data-testid={hols.length ? `roster-header-holiday-${d.format("YYYY-MM-DD")}` : undefined}
                    className={`px-2 py-2 text-center border-l border-slate-100 ${hols.length ? "bg-amber-50" : d.isSame(orgNowLocal(), "day") ? "bg-rose-50" : ""}`}>
                    <div className="text-[10px] font-bold uppercase tracking-wider text-slate-500">{d.format("ddd")}</div>
                    <div className="text-xs font-semibold text-slate-800">{d.format("D/M")}</div>
                    {hols.length > 0 && (
                      <div className="mt-0.5 inline-flex items-center gap-0.5 text-[9px] font-semibold text-amber-700">
                        <CalendarHeart className="h-2.5 w-2.5" />PH
                      </div>
                    )}
                  </div>
                );
              })}
            </div>

            {loading ? (
              <div className="p-16 flex justify-center"><Loader2 className="h-5 w-5 animate-spin text-slate-400" /></div>
            ) : (
              <div>
                {rows.map((row) => (
                  <div key={row.id || "unassigned"} className="grid border-b border-slate-100 last:border-0" style={{ gridTemplateColumns: `190px repeat(${days.length}, ${colW})` }}>
                    <div className="px-3 py-2 flex items-center gap-2 bg-slate-50/50 sticky left-0">
                      {row.problem ? <TriangleAlert className="h-4 w-4 text-red-600 shrink-0" /> : row.internal ? <Briefcase className="h-4 w-4 text-slate-500 shrink-0" /> : row.unassigned ? <UserX className="h-4 w-4 text-amber-500 shrink-0" /> : viewBy === "sil" ? (
                        <div className="h-6 w-6 rounded-md bg-[#bf3b4f] text-white flex items-center justify-center shrink-0"><Home className="h-3.5 w-3.5" /></div>
                      ) : row.photo ? (
                        <img src={row.photo} alt={row.name} className="h-6 w-6 rounded-full object-cover shrink-0" data-testid={`roster-photo-${row.id}`} />
                      ) : (
                        <div className={`h-6 w-6 rounded-full text-white flex items-center justify-center text-[10px] font-semibold shrink-0 ${viewBy === "participant" ? "bg-[#a32f42]" : "bg-slate-700"}`}>
                          {row.name.split(" ").map((w) => w[0]).slice(0, 2).join("")}
                        </div>
                      )}
                      <div className="min-w-0">
                        <div className={`text-xs font-medium truncate ${row.problem ? "text-red-700" : "text-slate-700"}`}>
                          <ContactCardTrigger kind={viewBy === "participant" ? "participant" : "staff"} id={row.id} name={row.name}
                            disabled={viewBy === "sil" || row.problem || row.unassigned || row.internal} />
                        </div>
                        {row.problem && (
                          <div className="text-[10px] text-red-600 font-semibold tabular-nums" data-testid="problem-row-count">
                            {problemCount} shift{problemCount === 1 ? "" : "s"} need a decision
                          </div>
                        )}
                        {row.role && <div className="text-[10px] text-slate-400 truncate">{row.role}</div>}
                        {viewBy === "participant" && row.holiday && <div className="text-[10px] text-slate-400 truncate" title="Holiday list" data-testid={`roster-holiday-${row.id}`}>{row.holiday}</div>}
                        {(viewBy === "sil" || row.internal) && row.sub && <div className="text-[10px] text-slate-400 truncate">{row.sub}</div>}
                        {viewBy === "staff" && !row.unassigned && workerHours[row.id] && workerHours[row.id].hours > 0 && (
                          <div className="text-[10px] font-semibold text-slate-500 tabular-nums" data-testid={`worker-hours-${row.id}`}
                            title="Active hours for the shown period — inactive sleepover time excluded">
                            {workerHours[row.id].hours.toFixed(1)}h{fortnight ? "/fn" : "/wk"}
                            {workerHours[row.id].on > 0 ? ` · ${workerHours[row.id].on} O/N` : ""}
                          </div>
                        )}
                      </div>
                    </div>
                    {days.map((d) => {
                      const items = cell(row.id, d);
                      const lv = leaveCell(row.id, d);
                      const uv = unavailCell(row.id, d);
                      const dds = d.format("YYYY-MM-DD");
                      const tmin = (t) => { const p = String(t || "").padStart(8, "0"); return (+p.slice(0, 2)) * 60 + (+p.slice(3, 5)); };
                      const pinned = []; // all-day items — always at the top
                      const timed = [];  // time-ranged items + shifts — sorted chronologically
                      const hol = viewBy === "participant" && row.holiday ? (holidayDays[row.holiday] || {})[dds] : null;
                      if (hol) pinned.push(
                        <div key="ph" data-testid={`roster-holiday-pill-${row.id}-${dds}`} title={`Public holiday (${row.holiday}): ${holidayText(hol)}`}
                          className="rounded-md px-1.5 py-1 text-[9px] font-semibold text-amber-800 bg-amber-100/70 flex items-start gap-1">
                          <CalendarHeart className="h-2.5 w-2.5 shrink-0 mt-px" /><span className="break-words leading-tight">{holidayText(hol)}</span>
                        </div>);

                      lv.forEach((l) => {
                        const approved = l.status === "Approved";
                        const expired = l.status === "Expired";
                        const bgColor = expired ? "#F8FAFC" : approved ? "#FEF2F2" : "#FFFBEB";
                        const borderColor = expired ? "#CBD5E1" : approved ? "#FCA5A5" : "#FCD34D";
                        const textColor = expired ? "#64748B" : approved ? "#B91C1C" : "#B45309";
                        const statusLabel = expired ? " · expired" : approved ? "" : " · pending";
                        const titleLabel = expired ? " (expired)" : approved ? "" : " (pending)";
                        const node = (
                          <div key={l.name} data-testid={`leave-pill-${row.id}-${dds}`}
                            title={`${l.leave_type} leave${titleLabel}${l.reason ? " · " + l.reason : ""}`}
                            className="rounded-md p-1.5 opacity-90"
                            style={{ backgroundColor: bgColor, border: `1px dashed ${borderColor}` }}>
                            <div className="flex items-start gap-1 text-[9px] font-semibold" style={{ color: textColor }}>
                              <CalendarOff className="h-2.5 w-2.5 shrink-0 mt-px" />
                              <span className="break-words leading-tight">{l.full_day ? "On leave" : `${String(l.start_time || "").padStart(8, "0").slice(0, 5)}–${String(l.end_time || "").padStart(8, "0").slice(0, 5)}`}</span>
                            </div>
                            <div className="text-[9px] text-slate-500 break-words leading-tight">{l.leave_type}{statusLabel}</div>
                          </div>
                        );
                        if (l.full_day) pinned.push(node);
                        else timed.push({ sort: tmin(l.start_time), node });
                      });

                      uv.forEach((u, i) => {
                        const maybe = u.availability_type === "Maybe Available";
                        const allDay = u.mode === "All Day" || u.mode === "All Day Except Overnight";
                        const timeLabel = u.mode === "All Day" ? (maybe ? "Maybe available" : "Unavailable")
                          : u.mode === "All Day Except Overnight" ? "Unavail (day)"
                          : `${String(u.start_time || "").padStart(8, "0").slice(0, 5)}–${String(u.end_time || "").padStart(8, "0").slice(0, 5)}`;
                        const node = (
                          <div key={`u${i}`} data-testid={`unavail-band-${row.id}-${dds}`}
                            title={`${maybe ? "Maybe available" : "Ongoing unavailability"} (${u.mode})${u.title ? " · " + u.title : ""}`}
                            className="rounded-md p-1.5 opacity-90" style={maybe ? { backgroundColor: "#FFFBEB", border: "1px dashed #FDE68A" } : { backgroundColor: "#FEF2F2", border: "1px dashed #FCA5A5" }}>
                            <div className="flex items-start gap-1 text-[9px] font-semibold" style={{ color: maybe ? "#92400E" : "#B91C1C" }}>
                              <CalendarX2 className="h-2.5 w-2.5 shrink-0 mt-px" />
                              <span className="break-words leading-tight">{maybe && u.mode !== "All Day" ? `Maybe ${timeLabel}` : timeLabel}</span>
                            </div>
                            <div className="text-[9px] text-slate-500 break-words leading-tight">{u.title || (maybe ? "Call before rostering" : "Ongoing pattern")}</div>
                          </div>
                        );
                        if (allDay) pinned.push(node);
                        else timed.push({ sort: tmin(u.start_time), node });
                      });

                      items.forEach(({ s, part }) => {
                        const sort = part === "end" ? 0 : dayjs(s.start_time).hour() * 60 + dayjs(s.start_time).minute();
                        timed.push({
                          sort,
                          shift: s.name,
                          travel: part === "start" ? null : travel[s.name],
                          node: <ShiftCard key={s.name + "-" + part} s={s} part={part} viewBy={viewBy} bulkMode={bulkMode} selected={selected.has(s.name)} hasIssue={issueSet.has(s.name)} rule={ruleIssues[s.name]}
                            legs={s.link_group_id ? linkGroups[s.link_group_id] : null}
                            travel={part === "start" ? null : travel[s.name]}
                            onClick={() => bulkMode ? toggleSel(s.name) : setDrawer({ open: true, name: s.name })} />,
                        });
                      });

                      timed.sort((a, b) => a.sort - b.sort);
                      return (
                        <div key={d.format()} onClick={() => items.length === 0 && openCreate(row.id, d)}
                          data-testid={`roster-cell-${row.id || "unassigned"}-${d.format("YYYY-MM-DD")}`}
                          className={`px-1 py-1 border-l border-slate-100 space-y-1 min-h-[56px] ${hol ? "bg-amber-50/60" : ""} ${items.length === 0 && can.has("roster.edit") && !bulkMode ? "cursor-pointer hover:bg-rose-50/40" : ""}`}>
                          {pinned}
                          {timed.map((t, i) => (
                            <div key={i}>
                              {t.node}
                              {t.travel && timed[i + 1]?.shift === t.travel.next_shift && (
                                <TravelConnector t={t.travel} />
                              )}
                            </div>
                          ))}
                        </div>
                      );
                    })}
                  </div>
                ))}
                {rows.length === 0 && <div className="p-16 text-center text-sm text-slate-400">No active {viewBy === "participant" ? "participants" : viewBy === "sil" ? "SIL houses" : "staff"}.</div>}
              </div>
            )}
          </div>
        </div>
      </div>

      <ShiftDrawer open={drawer.open} onOpenChange={(v) => setDrawer((d) => ({ ...d, open: v }))}
        shiftName={drawer.name} defaults={drawer.defaults} participants={participants} staff={staff} services={services} itemGroups={itemGroups} onSaved={refresh}
        onOpenShift={(name) => setDrawer({ open: true, name })} />

      <Dialog open={confirmPublish} onOpenChange={setConfirmPublish}>
        <DialogContent data-testid="bulk-publish-confirm-dialog" className="max-w-md">
          <DialogHeader><DialogTitle className="text-base">Publish these shifts?</DialogTitle></DialogHeader>
          {(() => {
            const total = selected.size;
            const unassigned = shifts.filter((s) => selected.has(s.name) && !s.support_worker).length;
            const assigned = total - unassigned;
            return (
              <div className="space-y-3 text-sm text-slate-700" data-testid="bulk-publish-summary">
                <p>You're about to publish <b>{total}</b> draft shift{total === 1 ? "" : "s"}.</p>
                <div className="rounded-lg border border-slate-200 bg-slate-50 divide-y divide-slate-100 text-[13px]">
                  <div className="flex items-center justify-between px-3 py-2"><span className="text-slate-500">Assigned to a worker</span><b className="text-emerald-700">{assigned}</b></div>
                  <div className="flex items-center justify-between px-3 py-2"><span className="text-slate-500">Unassigned (to the open queue)</span><b className={unassigned ? "text-amber-600" : "text-slate-400"}>{unassigned}</b></div>
                </div>
                {unassigned > 0 && <p className="text-[12px] text-amber-600">{unassigned} shift{unassigned === 1 ? "" : "s"} will be published without an assigned worker — they'll appear in the open/unassigned queue.</p>}
                {/* Email notifications — optional, opt-in only */}
                <NotifyCheckboxes testid="bulk-publish-notify"
                  value={{ staff: notifyStaff, participant: notifyParticipants }}
                  onChange={(v) => { setNotifyStaff(v.staff); setNotifyParticipants(v.participant); }}
                  labels={["Notify staff of new shifts", "Notify participants of new shifts"]}
                  hint="Each worker / participant gets one email listing their newly published shifts. Shifts with no worker are skipped for staff." />
              </div>
            );
          })()}
          <DialogFooter>
            <Button variant="outline" size="sm" onClick={() => { setConfirmPublish(false); setNotifyStaff(false); setNotifyParticipants(false); }} data-testid="bulk-publish-cancel">Cancel</Button>
            <Button size="sm" style={{ backgroundColor: "#0D9488" }} onClick={runBulkPublish} data-testid="bulk-publish-run">Publish {selected.size} shift{selected.size === 1 ? "" : "s"}</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <Dialog open={!!saveTmpl} onOpenChange={(v) => !v && setSaveTmpl(null)}>
        <DialogContent data-testid="save-template-dialog">
          <DialogHeader><DialogTitle className="text-base">Save this week as a template</DialogTitle></DialogHeader>
          {saveTmpl && (
            <div className="space-y-3">
              <p className="text-xs text-slate-500 -mt-2">Captures the shown week starting {start.format("ddd D MMM YYYY")} (Fri–Thu) — each shift's times and assigned worker — for {saveTmpl.kind === "sil" ? "a SIL house (all tenants' shifts)" : "one participant"}.</p>
              {saveTmpl.kind === "sil" ? (
                <div>
                  <label className="text-[11px] font-medium text-slate-600">SIL house</label>
                  <div className="mt-1"><Combobox testid="save-tmpl-property"
                    options={properties.map((p) => ({ value: p.name, label: p.property_name }))}
                    value={saveTmpl.property} onChange={(v) => setSaveTmpl((f) => ({ ...f, property: v, template: null }))} placeholder="Select SIL house" /></div>
                </div>
              ) : (
                <div>
                  <label className="text-[11px] font-medium text-slate-600">Participant</label>
                  <div className="mt-1"><Combobox testid="save-tmpl-participant"
                    options={participants.filter((p) => p.status === "Active").map((p) => ({ value: p.name, label: p.display_name || p.name }))}
                    value={saveTmpl.participant} onChange={(v) => setSaveTmpl((f) => ({ ...f, participant: v, template: null }))} placeholder="Select participant" /></div>
                </div>
              )}
              <div className="flex gap-2">
                <button type="button" onClick={() => setSaveTmpl((f) => ({ ...f, mode: "new" }))} data-testid="save-tmpl-mode-new"
                  className={`flex-1 h-8 rounded text-[11px] font-medium border ${saveTmpl.mode === "new" ? "bg-[#bf3b4f] text-white border-[#bf3b4f]" : "bg-white text-slate-600 border-slate-200"}`}>New template</button>
                <button type="button" onClick={() => setSaveTmpl((f) => ({ ...f, mode: "existing" }))} data-testid="save-tmpl-mode-existing"
                  className={`flex-1 h-8 rounded text-[11px] font-medium border ${saveTmpl.mode === "existing" ? "bg-[#bf3b4f] text-white border-[#bf3b4f]" : "bg-white text-slate-600 border-slate-200"}`}>Existing template</button>
              </div>
              <div className="grid grid-cols-2 gap-3">
                {saveTmpl.mode === "new" ? (
                  <div><label className="text-[11px] font-medium text-slate-600">New template name</label>
                    <Input value={saveTmpl.template_name} onChange={(e) => setSaveTmpl((f) => ({ ...f, template_name: e.target.value }))} className="h-9 text-xs mt-1" data-testid="save-tmpl-name" /></div>
                ) : (
                  <div><label className="text-[11px] font-medium text-slate-600">Existing template</label>
                    <select value={saveTmpl.template || ""} onChange={(e) => setSaveTmpl((f) => ({ ...f, template: e.target.value }))} className="h-9 text-xs rounded-md border border-slate-200 bg-white px-2 w-full mt-1" data-testid="save-tmpl-existing">
                      <option value="">Select…</option>
                      {tmplList.filter((t) => saveTmpl.kind === "sil" ? t.property === saveTmpl.property : t.participant === saveTmpl.participant).map((t) => <option key={t.name} value={t.name}>{t.template_name}</option>)}
                    </select></div>
                )}
                <div><label className="text-[11px] font-medium text-slate-600">Save into week</label>
                  <select value={saveTmpl.week} onChange={(e) => setSaveTmpl((f) => ({ ...f, week: e.target.value }))} className="h-9 text-xs rounded-md border border-slate-200 bg-white px-2 w-full mt-1" data-testid="save-tmpl-week">
                    {[1, 2, 3, 4].map((w) => <option key={w} value={w}>Week {w}</option>)}
                  </select></div>
              </div>
              {saveTmpl.mode === "existing" && (
                <div className="flex gap-2" data-testid="save-tmpl-weekmode">
                  <button type="button" onClick={() => setSaveTmpl((f) => ({ ...f, week_mode: "add" }))} data-testid="save-tmpl-add"
                    className={`flex-1 h-8 rounded text-[11px] font-medium border ${saveTmpl.week_mode === "add" ? "bg-teal-600 text-white border-teal-600" : "bg-white text-slate-600 border-slate-200"}`}>Add to week</button>
                  <button type="button" onClick={() => setSaveTmpl((f) => ({ ...f, week_mode: "overwrite" }))} data-testid="save-tmpl-overwrite"
                    className={`flex-1 h-8 rounded text-[11px] font-medium border ${saveTmpl.week_mode === "overwrite" ? "bg-teal-600 text-white border-teal-600" : "bg-white text-slate-600 border-slate-200"}`}>Overwrite week</button>
                </div>
              )}
              {saveTmpl.mode === "existing" && (
                <p className="text-[10px] text-slate-400">
                  {saveTmpl.week_mode === "add" ? "Adds these shifts to the week; you'll be asked how to resolve any clashes." : "Replaces everything in that template week with the shown week."}
                </p>
              )}
            </div>
          )}
          <DialogFooter>
            <Button variant="outline" size="sm" onClick={() => setSaveTmpl(null)}>Cancel</Button>
            <Button size="sm" style={{ backgroundColor: "#bf3b4f" }} onClick={doSaveTemplate} disabled={savingTmpl} data-testid="save-tmpl-confirm">
              {savingTmpl ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : "Save template"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <Dialog open={!!tmplConflicts} onOpenChange={(v) => !v && setTmplConflicts(null)}>
        <DialogContent data-testid="tmpl-conflict-dialog">
          <DialogHeader><DialogTitle className="text-base">Clashing shifts in the template week</DialogTitle></DialogHeader>
          <p className="text-xs text-slate-500 -mt-2">These day/time slots already exist in the template week. Which worker should be kept?</p>
          <div className="max-h-56 overflow-auto border border-slate-200 rounded-md">
            <table className="w-full text-[11px]">
              <thead><tr className="bg-slate-50 text-left text-slate-500"><th className="px-2 py-1.5">When</th><th className="px-2 py-1.5">On template</th><th className="px-2 py-1.5">From roster</th></tr></thead>
              <tbody>
                {(tmplConflicts || []).map((c, i) => (
                  <tr key={i} className="border-t border-slate-100" data-testid={`conflict-row-${i}`}>
                    <td className="px-2 py-1.5 whitespace-nowrap text-slate-600">{c.day} {c.start}–{c.end}</td>
                    <td className="px-2 py-1.5 text-slate-700">{c.template_worker}</td>
                    <td className="px-2 py-1.5 text-slate-700">{c.incoming_worker}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          <DialogFooter>
            <Button variant="outline" size="sm" onClick={() => setTmplConflicts(null)}>Cancel</Button>
            <Button variant="outline" size="sm" onClick={() => runSaveTemplate("keep_template")} disabled={savingTmpl} data-testid="conflict-keep-template">Keep template's shifts</Button>
            <Button size="sm" style={{ backgroundColor: "#bf3b4f" }} onClick={() => runSaveTemplate("keep_incoming")} disabled={savingTmpl} data-testid="conflict-keep-incoming">Keep the applied shifts</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}

const TRAVEL_TONE = {
  ok: { bg: "#F1F5F9", bd: "#CBD5E1", fg: "#475569" },
  tight: { bg: "#FFFBEB", bd: "#FDE68A", fg: "#92400E" },
  impossible: { bg: "#FEF2F2", bd: "#FCA5A5", fg: "#B91C1C" },
};

const travelTitle = (t) =>
  `About ${t.drive_min} min drive to the next shift with a ${t.gap_min} min gap` +
  (t.status === "impossible" ? " — the worker can't make it in time"
    : t.status === "tight" ? " — tight if the first shift runs over" : "");

// Connector shown between two stacked cards in the same day cell.
function TravelConnector({ t }) {
  const tone = TRAVEL_TONE[t.status] || TRAVEL_TONE.ok;
  return (
    <div className="flex items-center gap-1 px-1 py-0.5" title={travelTitle(t)}
      data-testid={`travel-connector-${t.next_shift}`}>
      <span className="h-3 w-px shrink-0" style={{ backgroundColor: tone.bd, marginLeft: "6px" }} />
      <span className="inline-flex items-center gap-0.5 rounded-full px-1 py-px text-[8px] font-bold leading-none"
        style={{ backgroundColor: tone.bg, border: `1px solid ${tone.bd}`, color: tone.fg }}>
        <Car className="h-2.5 w-2.5" />{t.drive_min} min · {t.gap_min} min gap
      </span>
    </div>
  );
}

function ShiftCard({ s, onClick, viewBy, bulkMode, selected, hasIssue, rule, part = "single", legs = null, travel = null }) {
  const st = boardStyle(s, hasIssue);
  // Went ahead despite a problem — keep it red and strike the time so it stays
  // obvious after it leaves the Problem row.
  const overridden = hasIssue && signedOff(s);
  // Override marks only apply while a problem still exists; once it clears the card is plain.
  const overrideShown = hasIssue && !!s.override_active;
  const kmOver = s.km_claimed > s.km_authorised && s.km_claimed > 0;
  const outOfRange = !!(s.clock_in_out_of_range || s.clock_out_out_of_range);
  const counterpart = viewBy === "participant" ? (s.support_worker_name || "Open") : (s.participant_name || s.participant || (s.no_participant ? (s.primary_billing_type === "Office" ? "Office Shift" : "Staff Training") : ""));
  const counterpartKind = viewBy === "participant" ? "staff" : "participant";
  const counterpartId = viewBy === "participant" ? s.support_worker : (s.no_participant ? null : s.participant);

  // Overnight shifts are split across two days with a matching zig-zag "torn"
  // edge so the two halves read as one shift (start = torn bottom, end = torn top).
  const isStart = part === "start", isEnd = part === "end";
  const enc = (c) => encodeURIComponent(c || "#ffffff");
  const teethBottom = (bg, bd) => `url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='12' height='9'%3E%3Cpath d='M0 0 H12 V2 L6 8 L0 2 Z' fill='${enc(bg)}'/%3E%3Cpath d='M0 2 L6 8 L12 2' fill='none' stroke='${enc(bd)}' stroke-width='1.4' stroke-linejoin='round'/%3E%3C/svg%3E")`;
  const teethTop = (bg, bd) => `url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='12' height='9'%3E%3Cpath d='M0 9 H12 V7 L6 1 L0 7 Z' fill='${enc(bg)}'/%3E%3Cpath d='M0 7 L6 1 L12 7' fill='none' stroke='${enc(bd)}' stroke-width='1.4' stroke-linejoin='round'/%3E%3C/svg%3E")`;
  const tearBorder = selected ? "#bf3b4f" : (st.dot || st.border);
  const timeLabel = isStart ? `${fmtTime(s.start_time)}–00:00` : isEnd ? `00:00–${fmtTime(s.end_time)}` : `${fmtTime(s.start_time)}–${fmtTime(s.end_time)}`;

  return (
    <button onClick={onClick} data-testid={`shift-card-${s.name}`} data-part={part}
      className="w-full text-left rounded-md p-1.5 transition-all hover:shadow-md relative"
      style={{
        backgroundColor: st.bg,
        border: `${selected ? 2 : (st.bw || 1)}px solid ${selected ? "#bf3b4f" : st.border}`,
        boxShadow: selected ? "0 0 0 2px #bf3b4f" : "none",
        ...(isStart ? { borderBottomLeftRadius: 0, borderBottomRightRadius: 0, borderBottom: "none" } : {}),
        ...(isEnd ? { borderTopLeftRadius: 0, borderTopRightRadius: 0, borderTop: "none" } : {}),
      }}>
      {isStart && (
        <span aria-hidden data-testid={`overnight-tear-bottom-${s.name}`} className="pointer-events-none absolute left-[-1px] right-[-1px] -bottom-[9px] h-[9px]"
          style={{ backgroundImage: teethBottom(st.bg, tearBorder), backgroundRepeat: "repeat-x", backgroundSize: "12px 9px" }} />
      )}
      {isEnd && (
        <span aria-hidden data-testid={`overnight-tear-top-${s.name}`} className="pointer-events-none absolute left-[-1px] right-[-1px] -top-[9px] h-[9px]"
          style={{ backgroundImage: teethTop(st.bg, tearBorder), backgroundRepeat: "repeat-x", backgroundSize: "12px 9px" }} />
      )}
      {overridden && (
        <span aria-hidden data-testid={`board-override-stripes-${s.name}`}
          className="pointer-events-none absolute top-0 right-0 rounded-tr-md rounded-bl-md px-1 py-px text-[7px] font-bold uppercase tracking-wider leading-none"
          style={{ backgroundColor: "rgba(220,38,38,0.15)", color: "#991B1B", border: "1px solid rgba(220,38,38,0.35)" }}>
          Override
        </span>
      )}
      <div className="relative flex items-center justify-between gap-1">
        <span className={`text-[10px] font-mono font-semibold tabular-nums inline-flex items-center gap-0.5`}
          style={{ color: st.text }}
          title={overridden ? "Approved despite a problem on this shift" : undefined}
          data-testid={overridden ? `board-approved-despite-${s.name}` : undefined}>
          {(isStart || isEnd) && <Moon className="h-2.5 w-2.5 shrink-0" title="Overnight shift (continues across two days)" />}
          {timeLabel}
        </span>
        <div className="flex items-center gap-0.5">
          {s.repeat_series_id && (
            <HoverCard openDelay={80} closeDelay={40}>
              <HoverCardTrigger asChild>
                <span data-testid={`board-recurring-${s.name}`} title="Part of a recurring series"
                  onClick={(e) => e.stopPropagation()}
                  className="inline-flex items-center rounded-full bg-violet-100 border border-violet-300 px-1 py-px text-[8px] font-bold leading-none text-violet-700 cursor-help hover:bg-violet-200">
                  ↻
                </span>
              </HoverCardTrigger>
              <HoverCardContent align="end" side="top" className="w-56 p-2.5" data-testid={`recurring-peek-${s.name}`}>
                <div className="text-[11px] font-semibold text-violet-900 mb-1 flex items-center gap-1">
                  ↻ Recurring series
                </div>
                <div className="text-[10px] text-slate-500 space-y-0.5">
                  <div>Click the shift to open the series panel for frequency, dates and cancel/edit options.</div>
                </div>
              </HoverCardContent>
            </HoverCard>
          )}
          {s.link_group_id && (
            <HoverCard openDelay={80} closeDelay={40}>
              <HoverCardTrigger asChild>
                <span data-testid={`board-linked-${s.name}`} title={`Linked package · leg ${s.link_seq || "?"}`}
                  onClick={(e) => e.stopPropagation()}
                  className="inline-flex items-center gap-0.5 rounded-full bg-indigo-100 border border-indigo-300 px-1 py-px text-[8px] font-bold leading-none text-indigo-700 cursor-help hover:bg-indigo-200">
                  <Link2 className="h-2.5 w-2.5" />{s.link_seq || ""}
                </span>
              </HoverCardTrigger>
              <HoverCardContent align="end" side="top" className="w-64 p-2.5" data-testid={`linked-peek-${s.name}`}>
                <div className="text-[11px] font-semibold text-indigo-900 mb-1.5 flex items-center gap-1">
                  <Link2 className="h-3 w-3" /> Linked package · {legs?.length || 0} legs
                </div>
                <div className="space-y-0.5">
                  {(legs || []).map((l) => (
                    <div key={l.name}
                      className={`flex items-center justify-between gap-2 rounded px-1.5 py-1 ${l.name === s.name ? "bg-indigo-50" : ""}`}>
                      <span className="font-mono tabular-nums text-[10px] text-slate-700 shrink-0">
                        {l.link_seq ? `${l.link_seq}. ` : ""}{fmtTime(l.start_time)}–{fmtTime(l.end_time)}
                      </span>
                      <span className="truncate text-[10px] text-slate-500">{l.support_worker_name || "Unassigned"}</span>
                    </div>
                  ))}
                  {(!legs || legs.length === 0) && <div className="text-[10px] text-slate-400">Loading legs…</div>}
                </div>
              </HoverCardContent>
            </HoverCard>
          )}
          {outOfRange && <MapPinOff className="h-3 w-3 text-orange-600" data-testid={`board-geo-${s.name}`} title="Clock GPS out of range — verify before payroll" />}
          {s.job_board_open ? <Briefcase className="h-3 w-3 text-violet-600" data-testid={`board-jobboard-${s.name}`} title="Posted to Job Board" /> : null}
          {hasIssue && (() => {
            const ri = rule?.issues || [];
            const blocks = ri.filter((i) => i.severity === "block");
            const warns = ri.filter((i) => i.severity === "warning");
            const hasDetail = ri.length > 0 || s.flag_reason;
            const AlertIcon = <TriangleAlert className={`h-3 w-3 ${rule?.block ? "text-red-600" : "text-red-600"}`} data-testid={`board-issue-${s.name}`} title={hasDetail ? undefined : "Needs attention"} />;
            if (!hasDetail) return AlertIcon;
            return (
              <HoverCard openDelay={80} closeDelay={40}>
                <HoverCardTrigger asChild>
                  <span onClick={(e) => e.stopPropagation()} className="cursor-help inline-flex">{AlertIcon}</span>
                </HoverCardTrigger>
                <HoverCardContent align="end" side="top" className="w-72 p-2.5" data-testid={`board-issue-peek-${s.name}`}>
                  {rule?.block && (
                    <div className="flex items-center gap-1.5 mb-2 rounded bg-red-50 border border-red-200 px-1.5 py-1" data-testid={`board-blocks-rostering-${s.name}`}>
                      <ShieldAlert className="h-3.5 w-3.5 text-red-700" />
                      <span className="text-[11px] font-bold uppercase tracking-wider text-red-700">Blocks rostering</span>
                    </div>
                  )}
                  <div className="space-y-1.5">
                    {blocks.map((i, k) => (
                      <div key={`b${k}`} className="text-[11px]">
                        <div className="font-semibold text-red-800">{RULE_LABELS[i.code] || i.message}</div>
                        {i.detail && <div className="text-slate-500 leading-snug">{i.detail}</div>}
                      </div>
                    ))}
                    {warns.map((i, k) => (
                      <div key={`w${k}`} className="text-[11px]">
                        <div className="font-semibold text-amber-700">{RULE_LABELS[i.code] || i.message}</div>
                        {i.detail && <div className="text-slate-500 leading-snug">{i.detail}</div>}
                      </div>
                    ))}
                    {s.flag_reason && <div className="text-[11px] text-slate-600"><span className="font-semibold">Flagged:</span> {s.flag_reason}</div>}
                  </div>
                </HoverCardContent>
              </HoverCard>
            );
          })()}
          {overridden && <ShieldAlert className="h-3 w-3 text-red-500"
            title={overrideShown ? "Manager override on this shift" : "Timesheet approved despite a problem"} />}
          {kmOver && <TriangleAlert className="h-3 w-3 text-amber-500" />}
          {bulkMode && <span className={`h-3 w-3 rounded-sm border ${selected ? "bg-[#bf3b4f] border-[#bf3b4f]" : "border-slate-300 bg-white"}`} />}
        </div>
      </div>
      <div className="text-[11px] font-semibold text-slate-800 truncate leading-tight">
        <ContactCardTrigger kind={counterpartKind} id={counterpartId} name={counterpart} disabled={bulkMode} />
        {viewBy === "participant" && s.worker_count > 1 && <span className="ml-1 text-[9px] text-[#bf3b4f] font-bold">+{s.worker_count - 1}</span>}
        {viewBy !== "participant" && s.participant_count > 1 && <span className="ml-1 text-[9px] text-[#bf3b4f] font-bold">+{s.participant_count - 1}</span>}
      </div>
      <div className="flex items-center gap-1 text-[10px] truncate" style={{ color: st.text }}>
        <span className="h-1.5 w-1.5 rounded-full shrink-0" style={{ backgroundColor: st.dot }} />
        {viewBy === "staff" && s.worker_count > 1 && (
          <span className="flex items-center shrink-0 font-bold text-slate-500" title={`${s.worker_count} workers on this shift`}
            data-testid={`shift-multi-worker-${s.name}`}>
            <Users className="h-2.5 w-2.5" />{s.worker_count}
            {s.billing_workers_mode === "each" && <span className="ml-0.5 text-violet-600" title="Billed for each worker">{s.worker_count}:1</span>}
            {s.billing_workers_mode === "shadow" && <span className="ml-0.5 text-violet-600" title="Shadow shift">shadow</span>}
          </span>
        )}
        <span className="truncate font-medium">{st.label}{isStart ? " · overnight →" : isEnd ? " · ← overnight" : ""}{s.link_group_id ? ` · linked (leg ${s.link_seq || "?"})` : ""}</span>
      </div>
      {!!s.flag_issue && !s.support_worker && (
        <div className="text-[9px] font-semibold text-amber-700 truncate" title={s.flag_reason || undefined}
          data-testid={`board-needs-refill-${s.name}`}>
          Lost its worker — needs refilling
        </div>
      )}
      {travel && (() => {
        const tone = TRAVEL_TONE[travel.status] || TRAVEL_TONE.ok;
        return (
          <div className="mt-1 flex items-center" title={travelTitle(travel)} data-testid={`travel-chip-${s.name}`}>
            <span className="inline-flex items-center gap-0.5 rounded-full px-1 py-px text-[8px] font-bold leading-none"
              style={{ backgroundColor: tone.bg, border: `1px solid ${tone.bd}`, color: tone.fg }}>
              <Car className="h-2.5 w-2.5" />{travel.drive_min} min to next
            </span>
          </div>
        );
      })()}
    </button>
  );
}
